import { useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useSwitchChain } from 'wagmi'
import { mainnet } from 'wagmi/chains'

import { useVisibility } from '@/domain/state/visibility'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { referralCodeLength, sparkPointsOnboardingDialogId } from '@/features/points/consts'
import { useConnectReferralCode } from '@/features/points/dialogs/points-onboarding-dialog/logic/useConnectReferralCode'
import { resolveActivePointsSeason } from '@/features/points/helpers'
import { pointsDataQueryKey } from '@/features/points/logic/useSparkPointsData'

const pointsSupportedChain = mainnet

interface UsePointsOnboardingDialogParams {
  closeDialog: () => void
}

export interface UsePointsOnboardingDialogResult {
  referralValue: string
  pointsSeason: number
  onCloseEvent: (event: Pick<Event, 'preventDefault'>) => void
  onReferralSkip: (() => void) | undefined
  onReferralChange: (value: string) => void
  action: {
    text: string
    error: string | undefined
    disabled: boolean
    onAction: () => void
    isPending: boolean
  }
}

export function usePointsOnboardingDialog({
  closeDialog,
}: UsePointsOnboardingDialogParams): UsePointsOnboardingDialogResult {
  const { account, chainId } = useConnectedAddress({ throwOnNotConnected: false })
  const { switchChain, isPending: isSwitchingChain } = useSwitchChain()

  const params = useParams({ from: '/points/{-$referralCode}' })
  const referralCode = params.referralCode ?? ''
  const [allowClosing, setAllowClosing] = useState(referralCode === '')
  const [referralValue, setReferralValue] = useState(referralCode)
  const { setVisibility } = useVisibility(sparkPointsOnboardingDialogId)
  const queryClient = useQueryClient()

  const {
    connectReferralCode,
    isPending: isConnecting,
    error,
    reset,
  } = useConnectReferralCode({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: pointsDataQueryKey({ account }),
      })
      closeDialog()
    },
    onError: () => setAllowClosing(true),
  })

  function onReferralSkip(): void {
    setVisibility(false)
    closeDialog()
  }

  function onCloseEvent(event: Pick<Event, 'preventDefault'>): void {
    event.preventDefault()
    if (!allowClosing) {
      setAllowClosing(true)
    }
  }

  function onReferralChange(value: string): void {
    setReferralValue(value.toUpperCase())
    reset()
  }

  const onContinue = account
    ? () => connectReferralCode({ account, referralCode: referralValue.toLowerCase() })
    : () => {}

  const action =
    chainId !== pointsSupportedChain.id
      ? {
          text: `Switch to ${pointsSupportedChain.name}`,
          onAction: () => switchChain({ chainId: pointsSupportedChain.id }),
          isPending: isSwitchingChain,
        }
      : {
          text: 'Continue',
          onAction: onContinue,
          isPending: isConnecting,
        }

  return {
    pointsSeason: resolveActivePointsSeason(),
    onCloseEvent,
    onReferralSkip: allowClosing ? onReferralSkip : undefined,
    referralValue,
    onReferralChange,
    action: {
      ...action,
      disabled: referralValue.length < referralCodeLength,
      error: getErrorMessage(error),
    },
  }
}

function getErrorMessage(error: Error | null): string | undefined {
  if (error && 'shortMessage' in error && typeof error.shortMessage === 'string') {
    return error.shortMessage
  }
  return error?.message
}
